// Pure parser for bulk question entry in the builder. One question per line,
// TAB-separated columns (paste straight from a spreadsheet):
//
//   kind <TAB> prompt <TAB> answerKey <TAB> choiceA ; choiceB ; choiceC
//
// Tabs (not "|") delimit fields precisely because the answer-key DSL USES "|"
// for alternation ("red|blue") — a pipe delimiter would split valid keys.
// - `kind` is free text mapped via mapQuestionKind ("tfng", "multiple choice"…).
// - `answerKey` (3rd column) is optional; if present it's DSL-validated.
// - `choices` (4th column, ; -separated) only matter for MCQ.
// Blank lines and lines starting with `#` are ignored.

import { mapQuestionKind, validateAnswerKey, type QuestionKind } from './extracted-structure';

export interface ParsedQuestion {
  lineNumber: number;
  kind: QuestionKind;
  rawKind: string;
  promptMd: string;
  choices: string[];
  /** Empty when no key was given or the key was invalid (see answerKeyWarning). */
  answerKey: string;
  answerKeyWarning?: string;
}

export interface ParseError {
  lineNumber: number;
  message: string;
}

export interface ParseResult {
  questions: ParsedQuestion[];
  errors: ParseError[];
}

export function parsePastedQuestions(text: string): ParseResult {
  const questions: ParsedQuestion[] = [];
  const errors: ParseError[] = [];

  const lines = text.split('\n');
  lines.forEach((rawLine, idx) => {
    const lineNumber = idx + 1;
    const line = rawLine.trim();
    if (line === '' || line.startsWith('#')) return;

    const parts = line.split('\t').map((p) => p.trim());
    const rawKind = parts[0] ?? '';
    const promptMd = parts[1] ?? '';
    const rawKey = parts[2] ?? '';
    const rawChoices = parts[3] ?? '';

    const kind = mapQuestionKind(rawKind);
    if (kind === null) {
      errors.push({ lineNumber, message: `unrecognised question type “${rawKind}”.` });
      return;
    }
    if (promptMd === '') {
      errors.push({ lineNumber, message: 'prompt (2nd field) is empty.' });
      return;
    }

    const choices = rawChoices
      .split(';')
      .map((c) => c.trim())
      .filter((c) => c !== '');
    if (kind === 'mcq' && choices.length < 2) {
      errors.push({ lineNumber, message: 'MCQ needs at least two “;”-separated choices.' });
      return;
    }

    let answerKey = '';
    let answerKeyWarning: string | undefined;
    if (rawKey !== '') {
      const check = validateAnswerKey(rawKey);
      if (check.ok) {
        answerKey = rawKey;
      } else {
        answerKeyWarning = check.warning ?? 'invalid answer key — left blank.';
      }
    }

    questions.push({
      lineNumber,
      kind,
      rawKind,
      promptMd,
      choices,
      answerKey,
      ...(answerKeyWarning !== undefined ? { answerKeyWarning } : {}),
    });
  });

  return { questions, errors };
}
